import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router-dom';
import React, { useCallback, useMemo, useState } from 'react';
import {
  ListingResource,
  useFieldUpdater,
  useFormProgressInit,
  useListingField,
  useListingStore,
} from '@brightmls/nextgen.lae.flc';
import {
  Documents,
  MediaCenter as MediaCenterComponent,
  MediaCenterProvider,
  Photos,
  type ResourceFields,
  type TermsOfServiceProvider,
  Tours,
} from '@brightmls/nextgen.lae.mediacenter';
import { useAuth } from '../../../contexts/AuthContext';
import { useFeatureFlags } from '../../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../../flags';
import { useTenant } from '../../../contexts/TenantContext';
import { useTenantPath } from '../../../hooks/useTenantPath';
import { PermissionKey } from '../../../system/permissions';
import { MediaCenterDataLoader } from './MediaCenterDataLoader';
import { useMediaCenterConfig } from './context/useMediaCenterConfig';
import { useMediaHandlers } from './context/useMediaHandlers';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ViewTermsDialog } from './components/ViewTermsDialog';
import { TermsContent } from './components/TermsContent';
import { PropertyField } from '../../../system/field';
import { ContentMinHeight } from '../../../components/common/ContentMinHeight';
import { useWebSocketContext } from '../../../contexts/WebSocketContext';
import { useMediaLogger } from './context/useMediaLogger';
import { useObjectDetectionFlow } from './context/useObjectDetectionFlow';
import { useMetadata } from '../../../contexts/MetadataContext';
import { TOTAL_STEPS } from '../../../components/edit/background/constants';

export const MediaCenterWrapper = () => {
  const navigate = useNavigate();
  const { template } = useMetadata();
  const { allowed } = useAuth();
  const { supportsClientResoWebApi } = useTenant();
  const { getPath } = useTenantPath();
  const { listingKey } = useParams<{ listingKey: string }>();
  const { connectionId } = useWebSocketContext();
  const logger = useMediaLogger('MediaCenterWrapper');

  // Create a field cache Map that will be populated with FLC field states
  const fieldCache = useMemo(() => new Map<string, any>(), []);

  // Populate field cache with fields needed by MediaCenter
  const autoHideField = useListingField(
    ListingResource.Property,
    PropertyField.AUTOMATICALLY_HIDE_MEDIA_OFF_MARKET_YN,
    false,
  );
  const visibleOffMarketKeysField = useListingField(
    ListingResource.Property,
    PropertyField.VISIBLE_WHEN_OFF_MARKET_MEDIA_KEYS,
    [] as number[],
  );

  const propertyTypeField = useListingField(
    ListingResource.Property,
    PropertyField.PROPERTY_TYPE,
    'Residential',
  );

  // Get listingId for AI processing optimization
  const listingIdField = useListingField(
    ListingResource.Property,
    PropertyField.LISTING_ID,
    '',
  );
  const listingId = listingIdField?.value as string | undefined;

  // Add fields to cache
  fieldCache.set('AUTOMATICALLY_HIDE_MEDIA_OFF_MARKET_YN', autoHideField);
  fieldCache.set(
    'VISIBLE_WHEN_OFF_MARKET_MEDIA_KEYS',
    visibleOffMarketKeysField,
  );
  fieldCache.set(PropertyField.LISTING_ID, listingIdField);

  // Field updater for arbitrary field writes (object detection save)
  const updateField = useFieldUpdater();

  // Resource adapter that uses FLC's field system
  const resource: ResourceFields = useMemo(
    () => ({
      get: (fieldName: string) => {
        // Check if field is in cache
        if (fieldCache.has(fieldName)) {
          return fieldCache.get(fieldName);
        }

        // Resolve arbitrary listing fields from the store
        const store = useListingStore.getState();
        const value = store.getResourceFieldValue(
          ListingResource.Property,
          fieldName,
          [],
        );

        return {
          value,
          updateValue: (newValue: any) => {
            updateField({ [fieldName]: newValue });
          },
          isDirty: false,
          isValid: true,
          errors: [],
          warnings: [],
          isHidden: false,
          isRequired: false,
          hasErrors: false,
          hasWarnings: false,
          isDisabled: false,
          renderCount: 0,
          addToList: [],
          removeFromList: [],
        };
      },
      set: (fieldName: string, value: any) => {
        const field = fieldCache.get(fieldName);
        if (field?.updateValue) {
          field.updateValue(value);
        } else {
          updateField({ [fieldName]: value });
        }
      },
    }),
    [fieldCache, updateField],
  );

  const config = useMediaCenterConfig(listingKey!, resource);
  const handlers = useMediaHandlers(listingKey!, { connectionId, listingId });

  const isResoSystem = supportsClientResoWebApi();
  const isAddEditPhotosAllowed = allowed({
    key: PermissionKey.UPLOAD_LISTING_PHOTOS,
  });
  const isAddEditDocumentsAllowed = allowed({
    key: PermissionKey.UPLOAD_DOCUMENTS,
  });
  const isAddEditToursAllowed = allowed({
    key: PermissionKey.ADD_EDIT_VIRTUAL_TOURS,
  });

  const tabs = [
    {
      path: config.routing?.paths?.photos || 'photos',
      label: 'Photos',
    },
    {
      path: config.routing?.paths?.documents || 'documents',
      label: 'Documents',
    },
  ];

  if (isResoSystem) {
    tabs.push({
      path: config.routing?.paths?.tours || 'tours',
      label: 'Virtual Tours',
    });
  }

  useFormProgressInit(template(propertyTypeField.value), TOTAL_STEPS);

  return (
    <ContentMinHeight>
      <Routes>
        <Route
          path="*"
          element={
            <MediaCenterProviderWrapper
              onBack={() =>
                navigate(getPath(`/edit/${listingKey}`), {
                  state: { from: 'media' },
                })
              }
              config={config}
              handlers={handlers}
              isResoSystem={isResoSystem}
              isAddEditPhotosAllowed={isAddEditPhotosAllowed}
              isAddEditDocumentsAllowed={isAddEditDocumentsAllowed}
              isAddEditToursAllowed={isAddEditToursAllowed}
              mediaTypeTabs={tabs}
            />
          }
        />
      </Routes>
    </ContentMinHeight>
  );
};

// New wrapper component that's rendered INSIDE a Route
interface MediaCenterProviderWrapperProps {
  config: any;
  handlers: any;
  onBack: () => void;
  mediaTypeTabs?: Array<{ path: string; label: string }>;
  isResoSystem: boolean;
  isAddEditPhotosAllowed: boolean;
  isAddEditDocumentsAllowed: boolean;
  isAddEditToursAllowed: boolean;
}

function MediaCenterProviderWrapper({
  config,
  handlers,
  mediaTypeTabs,
  isResoSystem,
  onBack,
  isAddEditPhotosAllowed,
  isAddEditDocumentsAllowed,
  isAddEditToursAllowed,
}: MediaCenterProviderWrapperProps) {
  const { listingKey } = useParams<{ listingKey: string }>();
  const { reanalyze } = useObjectDetectionFlow({
    listingKey: listingKey!,
    objectDetectionEnabled: config.features?.objectDetection ?? false,
  });

  const [viewTermsOpen, setViewTermsOpen] = useState(false);
  const handleViewTerms = useCallback(() => setViewTermsOpen(true), []);

  const { hasFeature } = useFeatureFlags();
  const showAICopy = hasFeature(FeatureFlags.NGW_AI_IMAGE_DESCRIPTIONS);

  const acceptCopy = showAICopy
    ? 'By clicking "Accept," you acknowledge and agree to the following terms regarding the use of media content and AI-generated descriptions on Bright MLS.'
    : 'By clicking "Accept," you acknowledge and agree to the following terms regarding the use of media content on Bright MLS.';

  const termsOfServiceProvider: TermsOfServiceProvider = useCallback(
    () => ({
      terms: <TermsContent showAICopy={showAICopy} acceptCopy={acceptCopy} />,
      accept: () => {},
      decline: () => onBack(),
    }),
    [showAICopy, acceptCopy, onBack],
  );

  return (
    <MediaCenterProvider
      config={config}
      {...handlers}
      onReanalyzePhotos={reanalyze}
      onViewTerms={handleViewTerms}
      termsOfServiceProvider={termsOfServiceProvider}
    >
      <MediaCenterDataLoader>
        <Routes>
          <Route
            element={
              <MediaCenterComponent
                onBack={onBack}
                mediaTypes={mediaTypeTabs}
              />
            }
          >
            <Route index element={<Navigate to="photos" replace />} />
            <Route
              path="photos"
              element={
                <ProtectedRoute allowed={isAddEditPhotosAllowed}>
                  <Photos />
                </ProtectedRoute>
              }
            />
            <Route
              path="documents"
              element={
                <ProtectedRoute allowed={isAddEditDocumentsAllowed}>
                  <Documents />
                </ProtectedRoute>
              }
            />
            {isResoSystem && (
              <Route
                path="tours"
                element={
                  <ProtectedRoute allowed={isAddEditToursAllowed}>
                    <Tours />
                  </ProtectedRoute>
                }
              />
            )}
          </Route>
        </Routes>
      </MediaCenterDataLoader>
      <ViewTermsDialog
        open={viewTermsOpen}
        onClose={() => setViewTermsOpen(false)}
      />
    </MediaCenterProvider>
  );
}
